import { Shape, Vector2, Vector3 } from "three";
import { EWall } from "../@types/Enums";
import { IBoundaryEdge } from "../models/abstract/BoundaryEdge";
import { IWall } from "../models/abstract/Wall";
import PhysicalBoundaryModel from "../models/standard/PhysicalBoundary";
import { ISpace } from "../models/standard/SpaceModel";
import StandardWallModel from "../models/standard/StandardWallModel";

const EPS = 1e-4;
const samePt = (a: Vector3, b: Vector3, eps = EPS) =>
  Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

export type OrderedEdge = { edge: IBoundaryEdge; s: Vector3; e: Vector3; dir: Vector3 };

function getRawEdgePointAt(e: IBoundaryEdge, t: number): Vector3 {
  if ((e.wall as any).pointAt) return (e.wall as any).pointAt(t);
  const a = (e.wall as any).startPoint as Vector3;
  const b = (e.wall as any).endPoint as Vector3;
  return new Vector3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);
}
// utils/boundaryUtils.ts — FINAL FINAL VERSION
import * as THREE from 'three';
import { isPointInPolygon } from './vectorUtils'; // adjust path if needed

const tempDir = new THREE.Vector3();
const tempNormal = new THREE.Vector3();
const tempToCenter = new THREE.Vector3();

export const getCorrectInnerEdge = (building: any, wallUuid: string): IBoundaryEdge | null => {
  let candidate: IBoundaryEdge | null = null;
  let bestCenter: THREE.Vector3 | null = null;

  // Find all full-segment edges for this wall
  for (const room of building.rooms ?? []) {
    for (const space of room.spaces ?? []) {
      const boundary = space.boundaries?.find((b: any) => b instanceof PhysicalBoundaryModel);
      if (!boundary?.edges) continue;

      const edge = boundary.edges.find((e: any) => 
        e?.wall?.uuid === wallUuid && 
        Math.abs(e.t0) < 0.01 && 
        Math.abs(e.t1 - 1) < 0.01
      );
      if (!edge) continue;

      // Compute approximate room center
      const mids = boundary.edges
        .filter((e: any) => e.startPoint && e.endPoint)
        .map((e: any) => e.startPoint.clone().add(e.endPoint.clone()).multiplyScalar(0.5));
      
      const center = new THREE.Vector3();
      mids.forEach(p => center.add(p));
      center.divideScalar(mids.length);

      // Wall direction: start → end
      tempDir.subVectors(edge.wall.endPoint, edge.wall.startPoint).normalize();

      // Normal pointing to Side A ("left" of wall direction)
      tempNormal.set(-tempDir.y, tempDir.x, 0);

      // Vector from edge midpoint to room center
      const edgeMid = edge.startPoint.clone().add(edge.endPoint.clone()).multiplyScalar(0.5);
      tempToCenter.subVectors(center, edgeMid);

      // If Side A points toward center → this edge is already correct
      if (tempNormal.dot(tempToCenter) > 0.01) {
        return edge; // PERFECT — inner edge found
      }

      // Otherwise: Side A points away → this is the OUTER edge
      // But we can FLIP it to make it inner!
      candidate = edge;
      bestCenter = center;
    }
  }

  if (!candidate) return null;

  // FLIP THE EDGE so that its direction matches wall start→end AND Side A is inner
  const flippedStart = candidate.endPoint.clone();
  const flippedEnd = candidate.startPoint.clone();

  // Mutate the edge (safe because it's already the correct geometry)
  candidate.setPoints(flippedStart, flippedEnd)
  candidate.reversed = !candidate.reversed; // if you use this flag

  // Optional: swap t0/t1
  [candidate.t0, candidate.t1] = [candidate.t1, candidate.t0];

  return candidate;
};
export const getCalculatedEdges = (space: ISpace): { start: Vector3; end: Vector3; wallUuid: string }[] => {
  const boundary = (space.boundaries || []).find(b => b instanceof PhysicalBoundaryModel) as PhysicalBoundaryModel;
  if (!boundary || !boundary.edges) return [];

  const edges = boundary.edges.filter(Boolean) as IBoundaryEdge[];
  if (edges.length < 3) return [];

  // Use the same robust ordering logic you had
  const ordered = orderEdges(edges);
  if (ordered.length < 3) return [];
  
  updateEdgesWithFloorGeometry(ordered); // Mutates the edges to fix corners

  return ordered.map(o => ({
    start: o.edge.startPoint.clone(),
    end: o.edge.endPoint.clone(),
    wallUuid: o.edge.wall.uuid
  }));
};

function orderEdges(edges: IBoundaryEdge[]): OrderedEdge[] {
  const es = (edges || []).filter(Boolean);
  if (es.length === 0) return [];
  const getRawGeometry = (ed: IBoundaryEdge) => {
    const pt0 = getRawEdgePointAt(ed, ed.t0);
    const pt1 = getRawEdgePointAt(ed, ed.t1);
    return ed.reversed ? { s: pt1.clone(), e: pt0.clone() } : { s: pt0.clone(), e: pt1.clone() };
  };
  const used = new Array(es.length).fill(false);
  const out: OrderedEdge[] = [];
  const geom0 = getRawGeometry(es[0]);
  used[0] = true;
  out.push({ edge: es[0], s: geom0.s, e: geom0.e, dir: geom0.e.clone().sub(geom0.s).normalize() });
  let lastEnd = geom0.e.clone();
  let remaining = es.length - 1;
  let iterations = 0;
  while (remaining > 0 && iterations < es.length * 2) {
    iterations++;
    let found = false;
    for (let i = 0; i < es.length; i++) {
      if (used[i]) continue;
      const geom = getRawGeometry(es[i]);
      if (samePt(lastEnd, geom.s)) {
        out.push({ edge: es[i], s: geom.s, e: geom.e, dir: geom.e.clone().sub(geom.s).normalize() });
        lastEnd = geom.e.clone(); used[i] = true; remaining--; found = true; break;
      } else if (samePt(lastEnd, geom.e)) {
        out.push({ edge: es[i], s: geom.e, e: geom.s, dir: geom.s.clone().sub(geom.e).normalize() });
        lastEnd = geom.s.clone(); used[i] = true; remaining--; found = true; break;
      }
    }
    if (!found) break;
  }
  return out;
}

function lineIntersection2D(p1: Vector2, p2: Vector2, p3: Vector2, p4: Vector2): Vector2 {
  const x1 = p1.x, y1 = p1.y, x2 = p2.x, y2 = p2.y, x3 = p3.x, y3 = p3.y, x4 = p4.x, y4 = p4.y;
  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(den) < 1e-9) return new Vector2(x2, y2);
  const px = ((x1*y2 - y1*x2) * (x3 - x4) - (x1 - x2) * (x3*y4 - y3*x4)) / den;
  const py = ((x1*y2 - y1*x2) * (y3 - y4) - (y1 - y2) * (x3*y4 - y3*x4)) / den;
  return new Vector2(px, py);
}
export function updateEdgesWithFloorGeometry(ordered: OrderedEdge[]): boolean {
  if (ordered.length < 3) return false;
  
  const offsetLines: Array<{ start: Vector2; end: Vector2 }> = [];
  
  // 1. Alle Wände von der Mittellinie nach innen versetzen
  for (let i = 0; i < ordered.length; i++) {
    const seg = ordered[i];
    const wall = seg.edge.wall;
    const side = seg.edge.side; // 'A' oder 'B'
    
    const wallDepth = (wall as any).depth ?? 0;
    const halfDepth = wallDepth / 2;
    
    const dir = seg.e.clone().sub(seg.s).normalize();
    const sFloor = seg.s.clone();
    const eFloor = seg.e.clone();
    
    // Die Normale zeigt nach RECHTS (relativ zur Laufrichtung der Edge)
    const normal = new THREE.Vector3(dir.y, -dir.x, 0); 
    
    // Im alten System: Side A war 0, Side B war -wallDepth (nach links).
    // Neues System (Center-Line): 
    // Side A rückt um halfDepth nach rechts (+), Side B um halfDepth nach links (-)
    const offsetDistance = side === 'A' ? halfDepth : -halfDepth;
    
    sFloor.add(normal.clone().multiplyScalar(offsetDistance));
    eFloor.add(normal.clone().multiplyScalar(offsetDistance));
    
    offsetLines.push({ 
      start: new THREE.Vector2(sFloor.x, sFloor.y), 
      end: new THREE.Vector2(eFloor.x, eFloor.y) 
    });
  }
  
  // 2. Schnittpunkte der nach innen versetzten Linien berechnen
  const verts: THREE.Vector3[] = [];
  const n = offsetLines.length;
  
  for (let i = 0; i < n; i++) {
    const curr = offsetLines[i];
    const next = offsetLines[(i + 1) % n];
    
    const inter = lineIntersection2D(curr.start, curr.end, next.start, next.end);
    
    // Fallback: Wenn Linien parallel sind oder der Schnittpunkt im Unendlichen liegt
    if (Math.abs(inter.x) > 100000 || Math.abs(inter.y) > 100000) {
      return false;
    }
    
    verts.push(new THREE.Vector3(inter.x, inter.y, 0));
  }
  
  // 3. Den Edges die neuen, perfekten Eckpunkte zuweisen
  for (let i = 0; i < ordered.length; i++) {
    const newStart = verts[i];
    const newEnd = verts[(i + 1) % n];
    const edge = ordered[i].edge;
    
    if (!samePt(edge.startPoint, newStart) || !samePt(edge.endPoint, newEnd)) {
      if (typeof edge.resetPoints === 'function') {
        edge.resetPoints();
      }
      if (typeof edge.setPoints === 'function') {
        edge.setPoints(newStart.clone(), newEnd.clone());
      }
    }
  }
  
  return true;
}

function orderWallsToPolyline(walls: StandardWallModel[]): Vector3[] {
  const ws = walls.filter(Boolean);
  if (ws.length === 0) return [];
  const used = new Array(ws.length).fill(false);
  function tryChain(reverseFirst: boolean): Vector3[] {
    const pts: Vector3[] = [];
    let count = 0;
    const w0 = ws[0];
    const currStart = reverseFirst ? w0.endPoint : w0.startPoint;
    let currEnd = reverseFirst ? w0.startPoint : w0.endPoint;
    pts.push(currStart.clone(), currEnd.clone());
    used[0] = true; count++;
    while (count < ws.length) {
      let extended = false;
      for (let i = 1; i < ws.length; i++) {
        if (used[i]) continue;
        const w = ws[i];
        if (samePt(currEnd, w.startPoint)) {
          pts.push(w.endPoint.clone());
          currEnd = w.endPoint; used[i] = true; count++; extended = true; break;
        }
        if (samePt(currEnd, w.endPoint)) {
          pts.push(w.startPoint.clone());
          currEnd = w.startPoint; used[i] = true; count++; extended = true; break;
        }
      }
      if (!extended) break;
    }
    return pts;
  }
  let pts = tryChain(false);
  if (pts.length - 1 < ws.length) { used.fill(false); pts = tryChain(true); }
  return pts;
}

function toClockwise(points: Vector3[]): Vector3[] {
  if (points.length < 3) return points;
  let area = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i], b = points[i + 1];
    area += a.x * b.y - b.x * a.y;
  }
  const a0 = points[0], b0 = points[points.length - 1];
  area += b0.x * a0.y - a0.x * b0.y;
  return area > 0 ? [...points].reverse() : points;
}

function shapeFromPolyline(points: Vector3[]): Shape {
  const shape = new Shape();
  if (points.length < 3) return shape;
  const first = points[0];
  const last = points[points.length - 1];
  const pts = samePt(first, last) ? points : [...points, first];
  const cw = toClockwise(pts);
  shape.moveTo(cw[0].x, cw[0].y);
  for (let i = 1; i < cw.length; i++) shape.lineTo(cw[i].x, cw[i].y);
  shape.closePath();
  return shape;
}

const POLY_TOL = 1e-4;
const SAMPLE_EPS = 1e-3;

export const shapeContainsPoint2D = (shape: THREE.Shape, p: THREE.Vector2): boolean => {
  const extracted = shape.extractPoints(12);
  const outer = extracted.shape;
  const holes = extracted.holes ?? [];

  if (!outer || outer.length < 3) return false;

  if (!isPointInPolygon(p, outer, POLY_TOL)) return false;

  for (const hole of holes) {
    if (!hole || hole.length < 3) continue;
    if (isPointInPolygon(p, hole, 0)) return false;
  }

  return true;
};

export function getSpaceUuidAtPointByGeometry(building: any, worldPos: THREE.Vector3): string | null {
  const rooms = building?.rooms ?? [];
  if (!rooms.length) return null;

  const base = new THREE.Vector2(worldPos.x, worldPos.y);
  const offsets: Array<[number, number]> = [
    [0, 0],
    [SAMPLE_EPS, 0], [-SAMPLE_EPS, 0],
    [0, SAMPLE_EPS], [0, -SAMPLE_EPS],
  ];

  let best: { spaceUuid: string; hits: number } | null = null;

  for (const room of rooms) {
    for (const space of room?.spaces ?? []) {
      const spaceUuid = space?.uuid;
      if (!spaceUuid) continue;

      let shape: THREE.Shape | null = null;
      try {
        // you already have this
        shape = getSpaceFloorShape(space);
      } catch {
        shape = null;
      }
      if (!shape) continue;

      let hits = 0;
      for (const [dx, dy] of offsets) {
        const pt = new THREE.Vector2(base.x + dx, base.y + dy);
        if (shapeContainsPoint2D(shape, pt)) hits++;
      }

      if (hits > 0 && (!best || hits > best.hits)) best = { spaceUuid, hits };
      if (best?.hits === offsets.length) return best.spaceUuid;
    }
  }

  return best?.spaceUuid ?? null;
}

/** * MAIN EXPORT: Calculates the floor shape using all offset logic.
 */
export function getSpaceFloorShape(spaceModel: any): Shape {
  const primaryBoundary = (() => {
    const arr = Array.isArray(spaceModel?.boundaries) ? spaceModel!.boundaries : [];
    return (arr.find((b: any) => b instanceof PhysicalBoundaryModel) as PhysicalBoundaryModel | undefined) ?? undefined;
  })();

  const edges: IBoundaryEdge[] =
    (primaryBoundary?.edges ?? []).filter(Boolean) as IBoundaryEdge[];

  const wallsArr = Array.isArray(spaceModel?.walls)
    ? (spaceModel!.walls as IWall[]).filter(Boolean)
    : [];

  const stdWalls = wallsArr.filter(
    (w) => w.type === EWall.STANDARD
  ) as StandardWallModel[];

  // 1. Try Boundary Edges (Prioritize complex shape)
  if (edges.length >= 3) {
    const ordered = orderEdges(edges);
    if (ordered.length >= 3) {
      const success = updateEdgesWithFloorGeometry(ordered);
      if (!success) {
        ordered.forEach((o) => {
          if (typeof o.edge.resetPoints === 'function') o.edge.resetPoints();
        });
      }

      if (primaryBoundary && primaryBoundary.edges) {
        const newEdgeOrder = ordered.map((o) => o.edge);
        if (
          primaryBoundary.edges.length !== newEdgeOrder.length ||
          primaryBoundary.edges.some((e, i) => e !== newEdgeOrder[i])
        ) {
          primaryBoundary.edges = newEdgeOrder;
        }
      }
      return shapeFromPolyline(ordered.map((o) => o.edge.startPoint));
    }
  }

  // 2. Fallback to Standard Walls
  if (stdWalls.length >= 3) {
    const polyline = orderWallsToPolyline(stdWalls);
    if (polyline.length >= 3) {
      if (!samePt(polyline[0], polyline[polyline.length - 1])) {
        polyline.push(polyline[0].clone());
      }
      return shapeFromPolyline(polyline);
    }
  }

  // 3. Empty Fallback
  return new Shape();
}